import React, { useState } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    Grid,
    Paper,
    Chip,
    Card,
    CardContent
} from '@mui/material';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
    useDroppable,
} from '@dnd-kit/core';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// 簡化的測試數據
const testItems = [
    { id: 1, text: "男孩特徵1", category: "boys", color: "#2196f3" },
    { id: 2, text: "女孩特徵1", category: "girls", color: "#e91e63" },
    { id: 3, text: "共同特徵1", category: "both", color: "#4caf50" },
];

interface TestItem {
    id: number;
    text: string;
    category: string;
    color: string;
    isPlaced?: boolean;
    placedIn?: string | null;
}

// 可拖拽的項目組件
function DraggableItem({ item }: { item: TestItem }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ 
        id: item.id,
        disabled: item.isPlaced
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            sx={{
                p: 2,
                backgroundColor: item.isPlaced ? item.color : 'white',
                color: item.isPlaced ? 'white' : 'black',
                border: `2px solid ${item.color}`,
                borderRadius: 2,
                cursor: item.isPlaced ? 'default' : 'grab',
                userSelect: 'none',
                minWidth: 120,
                textAlign: 'center',
                '&:active': {
                    cursor: item.isPlaced ? 'default' : 'grabbing',
                },
            }}
        >
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {item.text}
            </Typography>
            {item.isPlaced && (
                <Typography variant="caption" sx={{ display: 'block', color: 'white', opacity: 0.8 }}>
                    ✓ 已配對
                </Typography>
            )}
        </Paper>
    );
}

// 放置區域組件
function DropZone({ id, title, color, items }: { id: string; title: string; color: string; items: TestItem[] }) {
    const { isOver, setNodeRef } = useDroppable({
        id: id,
    });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                minHeight: 200,
                border: `3px dashed ${isOver ? color : '#ccc'}`,
                borderRadius: 2,
                p: 2,
                backgroundColor: isOver ? `${color}20` : '#fafafa',
                transition: 'all 0.3s ease',
            }}
        >
            <Typography variant="h6" sx={{ color, fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
                {title}
            </Typography>
            
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, textAlign: 'center' }}>
                已放置: {items.length} 個項目
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                {items.length > 0 ? (
                    items.map((item) => (
                        <DraggableItem key={item.id} item={item} />
                    ))
                ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        拖拽項目到這裡
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

export function PageGSQuizSimple() {
    const [items, setItems] = useState<TestItem[]>(() => 
        testItems.map((item) => ({
            ...item,
            isPlaced: false,
            placedIn: null
        }))
    );
    
    const [score, setScore] = useState(0);
    const [activeId, setActiveId] = useState<number | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    // 獲取各區域的項目
    const boysItems = items.filter(item => item.placedIn === 'boys');
    const girlsItems = items.filter(item => item.placedIn === 'girls');
    const bothItems = items.filter(item => item.placedIn === 'both');
    const unplacedItems = items.filter(item => !item.isPlaced);

    const handleDragStart = (event: DragStartEvent) => {
        console.log('🚀 Drag started:', event.active.id);
        setActiveId(event.active.id as number);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveId(null);

        console.log('🏁 Drag end event:', { active: active.id, over: over?.id });

        if (!over) {
            console.log('❌ No drop target found');
            return;
        }

        const itemId = active.id as number;
        const zoneId = over.id as string;
        
        console.log('📍 Drag end:', { itemId, zoneId });

        // 檢查配對是否正確
        const item = items.find(i => i.id === itemId);
        if (!item) {
            console.log('❌ Item not found');
            return;
        }

        let isCorrect = false;
        let newPlacedIn: string | null = null;

        if (zoneId === 'boys' && item.category === 'boys') {
            isCorrect = true;
            newPlacedIn = 'boys';
        } else if (zoneId === 'girls' && item.category === 'girls') {
            isCorrect = true;
            newPlacedIn = 'girls';
        } else if (zoneId === 'both' && item.category === 'both') {
            isCorrect = true;
            newPlacedIn = 'both';
        }

        console.log('🎯 Pairing result:', { isCorrect, newPlacedIn, itemCategory: item.category, zoneId });

        if (isCorrect) {
            console.log('✅ Correct pairing!');
            setItems(prev => prev.map(i => 
                i.id === itemId 
                    ? { ...i, isPlaced: true, placedIn: newPlacedIn }
                    : i
            ));
            setScore(prev => prev + 1);
        } else {
            console.log('❌ Incorrect pairing');
        }
    };

    const handleRestart = () => {
        setItems(testItems.map((item) => ({
            ...item,
            isPlaced: false,
            placedIn: null
        })));
        setScore(0);
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 3 }}>
                🧪 拖拽測試版本
            </Typography>
            
            <Typography variant="body1" align="center" sx={{ mb: 3, color: 'text.secondary' }}>
                簡化版本測試拖拽功能是否正常工作
            </Typography>

            {/* 分數顯示 */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Chip 
                    label={`得分: ${score} / ${testItems.length}`} 
                    color="primary" 
                    variant="outlined"
                    sx={{ fontSize: '1.1rem', px: 2, py: 1 }}
                />
            </Box>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                {/* 未放置的項目 */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        拖拽項目：
                    </Typography>
                    <Grid container spacing={2}>
                        {unplacedItems.map((item) => (
                            <Grid item key={item.id}>
                                <DraggableItem item={item} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 放置區域 */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <DropZone
                            id="boys"
                            title="男孩的變化"
                            color="#2196f3"
                            items={boysItems}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <DropZone
                            id="both"
                            title="共同變化"
                            color="#4caf50"
                            items={bothItems}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <DropZone
                            id="girls"
                            title="女孩的變化"
                            color="#e91e63"
                            items={girlsItems}
                        />
                    </Grid>
                </Grid>

                <DragOverlay>
                    {activeId ? (
                        <DraggableItem item={items.find(item => item.id === activeId)!} />
                    ) : null}
                </DragOverlay>
            </DndContext>

            {/* 控制按鈕 */}
            <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                    variant="contained"
                    onClick={handleRestart}
                    size="large"
                    sx={{ minWidth: 150 }}
                >
                    重新開始
                </Button>
            </Box>

            {/* 調試信息 */}
            <Card sx={{ mt: 3, backgroundColor: '#f5f5f5' }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        🔍 調試信息
                    </Typography>
                    <Typography variant="body2">
                        請打開瀏覽器開發者工具 (F12) 查看 Console 標籤中的拖拽日誌
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        當前狀態: 未放置項目 {unplacedItems.length} 個，已配對 {score} 個
                    </Typography>
                </CardContent>
            </Card>
        </Box>
    );
}

